import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock } from 'lucide-react';
import { SiteSettings } from '../types';

interface FooterProps {
  settings: SiteSettings;
}

const Footer: React.FC<FooterProps> = ({ settings }) => {
  return (
    <footer className="bg-theme-bg border-t border-theme-border pt-20 pb-32 md:pb-10 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <div className="mb-8">
            <h3 className="font-display text-2xl text-primary uppercase tracking-widest">{settings.clinicName}</h3>
          </div>
          <p className="text-theme-muted text-sm max-w-xs leading-relaxed mb-6">
            {settings.footerText}
          </p>
          <div className="flex gap-4">
            {/* Instagram: clinic_2650 */}
            <a 
              href="https://instagram.com/clinic_2650" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-10 h-10 rounded-full border border-theme-border flex items-center justify-center text-theme-muted hover:text-primary transition-colors"
              title="@clinic_2650"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </a>
            {/* Instagram: dran.decuyper */}
            <a 
              href="https://instagram.com/dran.decuyper" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-10 h-10 rounded-full border border-theme-border flex items-center justify-center text-theme-muted hover:text-primary transition-colors"
              title="@dran.decuyper"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </a>
          </div>
        </div>
        
        <div>
          <h4 className="text-theme-text font-bold text-xs uppercase tracking-[0.2em] mb-6">Contact</h4>
          <ul className="space-y-4 text-sm text-theme-muted">
            <li className="flex gap-3">
              <Phone className="text-primary w-4 h-4" />
              {settings.phone}
            </li>
            <li className="flex gap-3">
              <Mail className="text-primary w-4 h-4" />
              <span className="break-all">{settings.email}</span>
            </li>
            <li className="flex gap-3">
              <Clock className="text-primary w-4 h-4" />
              <span>Ma - Vr: 08:00 - 20:15</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-theme-text font-bold text-xs uppercase tracking-[0.2em] mb-6">Navigatie</h4>
          <ul className="space-y-3 text-sm text-theme-muted">
            <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
            <li><Link to="/botox-antwerpen" className="hover:text-primary transition-colors">Botox</Link></li>
            <li><Link to="/fillers-antwerpen" className="hover:text-primary transition-colors">Fillers</Link></li>
            <li><Link to="/tarieven" className="hover:text-primary transition-colors font-bold text-primary">Tarieven</Link></li>
            <li><Link to="/rimpelbehandeling-antwerpen" className="hover:text-primary transition-colors">Rimpelbehandeling</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-theme-text font-bold text-xs uppercase tracking-[0.2em] mb-6">Expertise</h4>
          <ul className="space-y-3 text-sm text-theme-muted">
            <li><Link to="/botox-antwerpen" className="hover:text-primary transition-colors">Botox Antwerpen</Link></li>
            <li><Link to="/fillers-antwerpen" className="hover:text-primary transition-colors">Fillers Antwerpen</Link></li>
            <li><Link to="/lippen-opvullen-antwerpen" className="hover:text-primary transition-colors">Lippen opvullen</Link></li>
            <li><Link to="/profhilo-antwerpen" className="hover:text-primary transition-colors">Profhilo Antwerpen</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-theme-text font-bold text-xs uppercase tracking-[0.2em] mb-6">Behandelingen</h4>
          <ul className="space-y-3 text-sm text-theme-muted">
            <li><Link to="/draadjeslift-antwerpen" className="hover:text-primary transition-colors">Draadjeslift</Link></li>
            <li><Link to="/laserbehandeling-antwerpen" className="hover:text-primary transition-colors">Laserbehandeling</Link></li>
            <li><Link to="/hydrafacial-antwerpen" className="hover:text-primary transition-colors">HydraFacial</Link></li>
            <li><Link to="/esthetische-geneeskunde-antwerpen" className="hover:text-primary transition-colors">Medische Esthetiek</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-theme-text font-bold text-xs uppercase tracking-[0.2em] mb-6">Juridisch</h4>
          <ul className="space-y-3 text-sm text-theme-muted">
            <li><Link to="/terms" className="hover:text-primary transition-colors">Voorwaarden</Link></li>
            <li><Link to="/privacy" className="hover:text-primary transition-colors">Privacy</Link></li>
            <li><Link to="/cookies" className="hover:text-primary transition-colors">Cookies</Link></li>
          </ul>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 mt-20 pt-8 border-t border-theme-border flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] text-theme-muted uppercase tracking-widest">
        <p>© 2024 {settings.clinicName}.</p>
        <a href="https://efficiotech.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
          Website by Efficiotech
        </a>
      </div>
    </footer>
  );
};

export default Footer;